import * as Sentry from "@sentry/react";
import { isSedeMode } from "./partnerStorage";
import { clearPartnerAccessState, hasPartnerAccessState } from "./partnerDirectory";
import { invalidateDelegatedToken } from "./effectiveToken";

// ---------------------------------------------------------------------------
// Keeps the partner state of this tab honest when the normal session changes
// in ANOTHER tab.
//
// The normal token is shared by every tab; sede mode, the partner session id
// and the directory are not. When another tab logs out, logs in as someone
// else or wipes the shared storage, what this tab holds belongs to a session
// that no longer exists: it is dropped at once instead of at the next request.
// A token re-issued to the SAME user (a renewal) is not another user: the
// partner state stays, only the delegated token minted under the old JWT goes.
//
// The browser only delivers `storage` events for changes made elsewhere, so
// the tab's own login and logout never arrive here. Like the rest of
// `partnerAccess`, this module only reads the storage of the normal session.
// ---------------------------------------------------------------------------

const NORMAL_TOKEN_KEY = "token";
// The normal boot decides what to show once the sede is gone.
const BOOT_ENTRY = "/";
const SENTRY_COMPONENT = "partnerAccess";

let installed = false;
// Given by whoever installs the guard; see `installCrossTabGuard`.
let notifySedeTabDropped = null;

// `null` when the storage cannot be used: then nothing can be told apart.
const persistentStorage = () => {
  try {
    return window.localStorage;
  } catch (e) {
    return null;
  }
};

// A whole-storage wipe arrives with a null key and takes the token with it.
const describeChange = (event) => {
  if (event.storageArea !== persistentStorage()) return null;
  if (event.key === null) return "cleared";
  if (event.key !== NORMAL_TOKEN_KEY || event.oldValue === event.newValue) return null;
  return event.newValue === null ? "removed" : "replaced";
};

const recordDrop = (change, wasSede) => {
  try {
    Sentry.addBreadcrumb({
      category: SENTRY_COMPONENT,
      message: "sede.crossTabSessionChanged",
      level: "info",
      data: { change, wasSede },
    });
  } catch (e) {
    // Telemetry must never keep the state of a session that ended.
  }
};

// The `id` claim of a JWT (base64url JSON payload, no signature check), or
// `null` for anything that does not decode to one. Only used to tell "same
// user, new token" from "another user": never trusted for anything else.
const userIdOf = (token) => {
  try {
    const payload = String(token).split(".")[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const { id } = JSON.parse(window.atob(padded));
    return typeof id === "string" && id !== "" ? id : null;
  } catch (e) {
    return null;
  }
};

const isSameUserReissue = (event) => {
  const previousId = userIdOf(event.oldValue);
  return previousId !== null && previousId === userIdOf(event.newValue);
};

const onStorageChange = (event) => {
  const change = describeChange(event);
  if (change === null || !hasPartnerAccessState()) return;

  if (change === "replaced" && isSameUserReissue(event)) {
    // Same user, new JWT: the sede and the partner session are still theirs;
    // the delegated token in memory was minted under the old JWT.
    invalidateDelegatedToken();
    return;
  }

  const wasSede = isSedeMode();
  if (wasSede && notifySedeTabDropped !== null) {
    try {
      notifySedeTabDropped();
    } catch (e) {
      // The state of a session that ended is dropped whatever the caller does.
    }
  }
  clearPartnerAccessState();
  recordDrop(change, wasSede);
  // Full reload on purpose: the screen was built as the sede's admin.
  if (wasSede) window.location.replace(BOOT_ENTRY);
};

/**
 * Starts listening for session changes made in other tabs. Meant for the boot
 * of the page; calling it again does nothing. `onSedeTabDropped` runs when a
 * tab that was working a sede is about to lose its state and reload, before
 * the state is cleared.
 */
export const installCrossTabGuard = ({ onSedeTabDropped } = {}) => {
  if (installed) return;
  installed = true;
  notifySedeTabDropped = typeof onSedeTabDropped === "function" ? onSedeTabDropped : null;
  window.addEventListener("storage", onStorageChange);
};
